<template>
  <v-card color="grey lighten-4 mb-2">
    <v-card-text class="white pa-0">
      <div
        id="rms-strategy-table"
        class="grid-container"
        :class="getClassContainer()"
      >
        <table v-if="group_of_columns">
          <thead>
            <tr v-if="density === 'default'" class="group-row">
              <th
                v-for="group in group_names"
                :key="group"
                :colspan="group_of_columns[group].length"
                :class="getClassGroupRowTh(group)"
              >
                <template v-if="group">
                  <div
                    class="column-group grey lighten-2 black--text caption py-1"
                  >
                    <div
                      v-if="group_of_columns[group].length === 1"
                      class="d-flex justify-center align-center"
                      style="gap: 5px"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" color="black" small>
                            {{ configStrategyTable[group].icon }}
                          </v-icon>
                        </template>
                        <span>{{ $t(group) }}</span>
                      </v-tooltip>
                      <template v-if="group === 'sellpar'">
                        <span>{{ $t(group) }}</span>
                        <v-icon
                          v-if="is_updating_sellpar"
                          size="14"
                          color="black"
                          class="sellpar-spin"
                          :title="$t('sellpar_updating')"
                        >
                          mdi-loading
                        </v-icon>
                        <v-tooltip v-else bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              @click="openSellparSettings"
                              x-small
                              icon
                              color="grey"
                            >
                              <v-icon size="14">mdi-cog-outline</v-icon>
                            </v-btn>
                          </template>
                          {{ $t("sellpar_settings") }}
                        </v-tooltip>
                      </template>
                    </div>
                    <template v-else>
                      <div
                        class="d-flex justify-center align-center"
                        style="gap: 5px"
                      >
                        <v-icon color="black" size="14">
                          {{ configStrategyTable[group].icon }}
                        </v-icon>
                        <span>
                          <template
                            v-if="
                              group.includes('pickup_') && group !== 'pickup_0'
                            "
                          >
                            Pickup
                            {{ current_hotel.settings.pickup_spots[group] }}
                          </template>
                          <template v-else>
                            {{ $t(group) }}
                          </template>
                        </span>
                        <template v-if="group === 'rates'">
                          <span v-show="showBulkUploadButton">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  @click="show_bulk_upload_dialog = true"
                                  v-on="on"
                                  x-small
                                  icon
                                  color="black"
                                >
                                  <v-icon>mdi-upload-outline</v-icon>
                                </v-btn>
                              </template>
                              {{ $t("bulk_upload") }}
                            </v-tooltip>
                          </span>
                          <span
                            v-show="showBulkUploadProgress"
                            style="width:100px;"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <div
                                  class="bulk-upload-progress mt-0 ml-3"
                                  v-on="on"
                                >
                                  <v-progress-linear
                                    :value="
                                      bulkPvpMlosStatus
                                        ? bulkPvpMlosStatus.percent
                                        : 0
                                    "
                                    color="secondary lighten-2"
                                    background-color="white"
                                  ></v-progress-linear>
                                </div>
                              </template>
                              {{ $t("bulk_upload_running") }}
                            </v-tooltip>
                          </span>
                        </template>
                      </div>
                    </template>
                  </div>
                </template>
              </th>
            </tr>
            <tr class="header-row">
              <th
                v-for="column_header in column_headers"
                :key="`${column_header.group}-${column_header.column}`"
                :class="getClassHeaderRowTh(column_header)"
              >
                <div
                  class="cell d-flex justify-center flex-column"
                  :class="getClassHeaderRowCell(column_header)"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                        class="d-block day-name grey--text text--darken-3 text-uppercase text-truncate"
                      >
                        <template v-if="column_header.group === 'segment'">
                          {{ getSegmentName(column_header) }}
                        </template>
                        <template
                          v-else-if="column_header.group === 'roomtype'"
                        >
                          <div
                            class="d-flex align-center justify-center"
                            style="gap: 5px"
                          >
                            <span class="text-truncate">
                              {{ getRoomTypeName(column_header) }}
                            </span>
                            <v-avatar size="18" color="grey lighten-2">
                              {{ getRoomTypeAvailableRooms(column_header) }}
                            </v-avatar>
                          </div>
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group === 'cityMKD' &&
                              column_header.column === 'MKD-Final'
                          "
                        >
                          {{ $t("cityMKD") }}
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group === 'pickup_0' &&
                              column_header.column === 'sold_rooms'
                          "
                        >
                          PU 0 RN
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group === 'pickup_0' &&
                              column_header.column === 'avg_price'
                          "
                        >
                          PU 0 ADR
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group === 'inventory' &&
                              column_header.column === 'available_pace'
                          "
                        >
                          {{ $t("pace") }}
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group.includes('pickup_') &&
                              column_header.group !== 'pickup_0' &&
                              column_header.column === 'sold_rooms'
                          "
                        >
                          PU
                          {{
                            current_hotel.settings.pickup_spots[
                              column_header.group
                            ]
                          }}
                          RN
                        </template>
                        <template
                          v-else-if="
                            density === 'compact' &&
                              column_header.group.includes('pickup_') &&
                              column_header.group !== 'pickup_0' &&
                              column_header.column === 'avg_price'
                          "
                        >
                          PU
                          {{
                            current_hotel.settings.pickup_spots[
                              column_header.group
                            ]
                          }}
                          ADR
                        </template>
                        <template
                          v-else-if="
                            column_header.group === 'rates' &&
                              column_header.column === 'rate_change'
                          "
                        >
                          <div class="d-flex align-center justify-center">
                            <span class="title-column">
                              {{ $t(column_header.column) }}
                            </span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  icon
                                  x-small
                                  color="secondary"
                                  class="ml-1 apply-prices-btn"
                                  :loading="loading_bulk_apply_prices"
                                  :disabled="
                                    applyChangesList.length === 0 &&
                                      applySuggestionsList.length === 0
                                  "
                                  @click="bulk_apply_prices_dialog = true"
                                >
                                  <v-icon size="12">mdi-send-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t("apply_all_rate_changes") }}</span>
                            </v-tooltip>
                          </div>
                          <v-dialog
                            v-model="bulk_apply_prices_dialog"
                            :fullscreen="$vuetify.breakpoint.xsOnly"
                            max-width="400"
                            scrollable
                          >
                            <v-card>
                              <v-card-title
                                center
                                class="headline justify-center"
                              >
                                {{ $t("bulk_apply_prices_title") }}
                              </v-card-title>
                              <v-card-text>
                                <div class="text-body-1 text-center mb-4">
                                  {{ $t("bulk_apply_prices_text") }}
                                </div>
                                <div v-if="applyCombinedList.length > 0">
                                  <div class="text-subtitle-2 mb-2">
                                    {{ $t("Prices") }}:
                                  </div>
                                  <ul class="text-body-1">
                                    <li
                                      v-for="item in applyCombinedList"
                                      :key="`${item.type}-${item.date}`"
                                    >
                                      <span> {{ formatDate(item.date) }}</span>
                                      <span class="mr-1">:</span>
                                      <span
                                        class="text-decoration-line-through"
                                      >
                                        {{ formatPrice(item.price_current) }}
                                      </span>
                                      <span class="ml-1 mr-1">
                                        <v-icon small>
                                          mdi-arrow-right-bold
                                        </v-icon>
                                      </span>
                                      <b>{{ formatPrice(item.price) }}</b>
                                    </li>
                                  </ul>
                                </div>
                              </v-card-text>
                              <v-card-actions class="justify-center pb-4">
                                <v-btn
                                  color="secondary"
                                  rounded
                                  depressed
                                  class="pl-5 pr-5"
                                  @click="bulk_apply_prices()"
                                >
                                  {{ $t("apply") }}
                                </v-btn>
                                <v-btn
                                  text
                                  rounded
                                  depressed
                                  class="pl-5 pr-5"
                                  @click="bulk_apply_prices_dialog = false"
                                >
                                  {{ $t("cancel") }}
                                </v-btn>
                              </v-card-actions>
                            </v-card>
                          </v-dialog>
                        </template>
                        <template v-else>
                          {{ $t(column_header.column) }}
                        </template>
                      </span>
                    </template>
                    <span>
                      {{ getColumnTitle(column_header) }}
                    </span>
                  </v-tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <aRmsStrategyDayTableRow
              v-for="day in days"
              :key="`${day}`"
              class="row-data"
              :date="day"
              :row="columns[day]"
              :nonStickyColumnHeaders="non_sticky_column_headers"
              :lastStickyColumn="last_sticky_column"
              :configStrategyTable="configStrategyTable"
              :lastColumnOfEachGroup="last_column_of_each_group"
              :kpiMode="kpiMode"
              :density="density"
              @clickOnDateCell="getDayDetails(day)"
              @hoveredKpi="kpi_hover = $event"
              @price-selection-changed="handlePriceSelectionChanged"
              @suggestion-selection-changed="handleSuggestionSelectionChanged"
            />
            <aRmsStrategyDayTableRow
              class="row-data summary"
              :date="null"
              :row="totalPeriod"
              :nonStickyColumnHeaders="non_sticky_column_headers"
              :lastStickyColumn="last_sticky_column"
              :configStrategyTable="configStrategyTable"
              :lastColumnOfEachGroup="last_column_of_each_group"
              :kpiMode="kpiMode"
              :density="density"
              isSummary
              @hoveredKpi="kpi_hover = $event"
            />
          </tbody>
        </table>
      </div>
    </v-card-text>
    <a-chm-bulk-upload-dialog
      v-if="has_permission('rms_rates_bulk_upload')"
      :show="show_bulk_upload_dialog"
      @close="closeBulkUploadDialog"
    ></a-chm-bulk-upload-dialog>
    <v-dialog
      v-model="show_sellpar_settings_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
      scrollable
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("sellpar_settings") }}
        </v-card-title>
        <v-card-text>
          <v-row class="pt-3">
            <v-col cols="12" class="py-1">
              <v-text-field
                v-model.number="sellpar_hotel_tax"
                :label="$t('sellpar_hotel_tax')"
                type="number"
                min="0"
                max="100"
                suffix="%"
                :rules="sellparSettingsRules"
                outlined
                rounded
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="py-1">
              <v-text-field
                v-model.number="sellpar_hotel_commission"
                :label="$t('sellpar_hotel_commission')"
                type="number"
                min="0"
                max="100"
                suffix="%"
                :rules="sellparSettingsRules"
                outlined
                rounded
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            :loading="saving_sellpar_settings"
            :disabled="!isSellparSettingsValid"
            @click="saveSellparSettings"
          >
            {{ $t("save") }}
          </v-btn>
          <v-btn
            text
            rounded
            depressed
            class="pl-5 pr-5"
            :disabled="saving_sellpar_settings"
            @click="show_sellpar_settings_dialog = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import aChmBulkUploadDialog from "@/components/channel-manager/aChmBulkUploadDialog.vue";
import aRmsStrategyDayTableRow from "@/components/rms/axel/aRmsStrategyDayTableRow";
import HotelService from "@/services/HotelService.js";
import {
  getFriendlyDate,
  getFriendlyMonthStrategy,
  rateChangeLogSnackbarExtras,
  showError
} from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
var numeral = require("numeral");

let default_sticky_group_columns = ["", "OTB"];

export default {
  name: "a-rms-strategy-day-table",
  components: { aChmBulkUploadDialog, aRmsStrategyDayTableRow },
  props: {
    configStrategyTable: Object,
    columns: Object,
    totalPeriod: Object,
    days: Array,
    roomTypes: Array,
    segments: Array,
    kpiMode: String,
    density: String
  },
  emits: ["getDayDetails"],
  data: () => ({
    table_class: "rms-strategy-day-table",
    filtered_rows: [],
    filtered_columns: [],
    kpi_hover: "",
    bulk_apply_prices_dialog: false,
    selected_prices: {},
    selected_suggestions: {},
    loading_bulk_apply_prices: false,
    show_bulk_upload_dialog: false,
    show_sellpar_settings_dialog: false,
    sellpar_hotel_tax: 10,
    sellpar_hotel_commission: 25,
    saving_sellpar_settings: false,
    is_updating_sellpar: false,
    sellpar_task_id: null,
    sellpar_poll_timeout: null
  }),
  beforeDestroy() {
    this.clearSellparPolling();
  },
  methods: {
    getDayDetails(v) {
      this.$emit("getDayDetails", v);
    },
    getFriendlyMonthStrategy(date) {
      return getFriendlyMonthStrategy(date, true);
    },
    getHotelComparativeDates() {
      // Optimizamos para pedir solo el rango de fechas que estamos visualizando
      // en lugar de forzar la carga de años completos.
      const startDate = Vue.moment(this.days[0]);
      const endDate = Vue.moment(this.days[this.days.length - 1]);

      this.$store
        .dispatch("getHotelComparativeDates", {
          hotel_id: this.current_hotel.id,
          // Mantenemos el año del inicio como referencia principal para el backend
          year: startDate.format("YYYY"),
          from_date: startDate.format("YYYY-MM-DD"),
          to_date: endDate.format("YYYY-MM-DD")
        })
        .catch(() => {
          showError(this, "something_went_wrong");
        });
    },
    // Funciones para las clases, para tener el codigo mas limpio --------------------------------------------------
    getClassContainer() {
      if (this.density === "compact") {
        return "density-compact";
      }
      return "";
    },
    getClassGroupRowTh(group) {
      let classes = [];
      if (group === this.last_sticky_group_column) {
        classes.push("sticky-columns-shadow");
      }
      if (group !== this.last_sticky_group_column) {
        classes.push("br-bolder");
      }
      if (group === "OTB") {
        classes.push("group-otb");
      }
      return classes;
    },
    getClassHeaderRowTh(column_header) {
      let classes = [];
      if (column_header.column === this.last_sticky_group_column) {
        classes.push("sticky-columns-shadow");
      }
      if (
        this.last_column_of_each_group[column_header.group] ===
          column_header.column &&
        column_header.group !== this.last_sticky_group_column
      ) {
        classes.push("br-bolder");
      }
      if (column_header.group === "OTB") {
        classes.push("header-otb");
      }
      return classes;
    },
    getClassHeaderRowCell(column_header) {
      let classes = [];
      if (!this.sticky_columns.includes(column_header.column)) {
        classes.push("kpi-row");
      }
      if (
        this.kpi_hover.column === column_header.column &&
        this.kpi_hover.group === column_header.group
      ) {
        classes.push("kpi-hovered");
      }
      if (
        column_header.group === "rates" &&
        column_header.column === "rate_change"
      ) {
        classes.push("kpi-rate-change");
      }
      return classes;
    },
    // Funciones para las clases, para tener el codigo mas limpio --------------------------------------------------
    getSegmentName(column_header) {
      return this.segments.find(
        segment => segment.code === column_header.column
      ).name;
    },
    getRoomTypeName(column_header) {
      return this.roomTypes.find(
        roomType => roomType.code === column_header.column
      ).name;
    },
    getRoomTypeAvailableRooms(column_header) {
      return this.roomTypes.find(
        roomType => roomType.code === column_header.column
      ).available_rooms;
    },
    bulk_apply_prices() {
      this.bulk_apply_prices_dialog = false;
      this.loading_bulk_apply_prices = true;

      const promises = [];
      let partialFailure = false;
      let timedOut = false;

      if (this.applyChangesList.length > 0) {
        const changes = this.applyChangesList.map(item => ({
          date: item.date,
          pvp: item.price
        }));
        promises.push(
          this.$store.dispatch("setRoomTypeDayRatesBulk", changes).then(res => {
            const results = res.results || {};
            const failed = Object.entries(results).filter(
              ([, r]) => r.status !== "ok"
            );
            if (failed.length > 0) {
              partialFailure = true;
              const failedDates = failed.map(([date]) => date);
              const newSelected = {};
              failedDates.forEach(date => {
                if (this.selected_prices[date]) {
                  newSelected[date] = this.selected_prices[date];
                }
              });
              this.selected_prices = newSelected;
              this.$store.dispatch("setSnackbar", {
                title: this.$t("changes_applied_partially", {
                  ok: Object.keys(results).length - failed.length,
                  total: Object.keys(results).length
                }),
                color: "warning"
              });
            }
          })
        );
      }

      if (this.applySuggestionsList.length > 0) {
        const data = {
          hotel_id: this.current_hotel.id,
          suggestions: this.applySuggestionsList.map(item => ({
            id: item.id,
            price: item.price
          }))
        };
        promises.push(
          this.$store.dispatch("applyPricingStrategySuggestions", data)
        );
      }

      Promise.all(promises)
        .then(() => {
          if (!partialFailure) {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("changes_applied_successfully"),
              color: "accent",
              ...rateChangeLogSnackbarExtras(this)
            });
            this.selected_prices = {};
            this.selected_suggestions = {};
          } else {
            this.selected_suggestions = {};
          }
        })
        .catch(error => {
          // Distingim un timeout (la petició ha tardat massa) d'un error real.
          // En un timeout el backend pot estar aplicant els canvis igualment, així
          // que NO mostrem l'error dur "Error al aplicar los cambios".
          const isTimeout =
            !!error &&
            (error.code === "ECONNABORTED" ||
              (typeof error.message === "string" &&
                error.message.toLowerCase().includes("timeout")));

          if (isTimeout) {
            timedOut = true;
            this.$store.dispatch("setSnackbar", {
              title: this.$t("changes_taking_long"),
              color: "warning"
            });
          } else {
            showError(this, "changes_not_applied");
          }
        })
        .finally(() => {
          this.loading_bulk_apply_prices = false;
          // En cas de timeout NO recarreguem ni netegem la selecció: així mantenim
          // els tics marcats i l'usuari pot tornar a aplicar els que faltin (l'acció
          // és idempotent). Recarregar reinicialitzaria els checkboxes i obligaria a
          // tornar-los a marcar un per un.
          if (!timedOut) {
            this.$store.dispatch("setReloadSubscribedModules");
          }
        });
    },
    handlePriceSelectionChanged(payload) {
      if (payload.selected) {
        this.$set(this.selected_prices, payload.date, {
          date: payload.date,
          price: payload.price,
          price_current: payload.price_current
        });
      } else {
        this.$delete(this.selected_prices, payload.date);
      }
    },
    handleSuggestionSelectionChanged(payload) {
      if (payload.selected) {
        this.$set(this.selected_suggestions, payload.date, {
          id: payload.suggestion.id,
          date: payload.date,
          price: payload.price,
          price_current: payload.price_current
        });
      } else {
        this.$delete(this.selected_suggestions, payload.date);
      }
    },
    formatPrice(price) {
      return (
        numeral(price).format(this.l.number0_format) + this.l.money_post_symbol
      );
    },
    formatDate(date) {
      return getFriendlyDate(date, true);
    },
    bulkImportStatus() {
      if (!this.has_permission("rms_rates_bulk_upload")) return;
      this.$store.dispatch("fetchBulkPvpMlosStatus");
    },
    closeBulkUploadDialog() {
      this.show_bulk_upload_dialog = false;
      setTimeout(() => {
        this.bulkImportStatus();
      }, 1000);
    },
    openSellparSettings() {
      const settings =
        (this.current_hotel && this.current_hotel.settings) || {};
      this.sellpar_hotel_tax =
        settings.sellpar_hotel_tax !== undefined &&
        settings.sellpar_hotel_tax !== null
          ? Number(settings.sellpar_hotel_tax)
          : 10;
      this.sellpar_hotel_commission =
        settings.sellpar_hotel_commission !== undefined &&
        settings.sellpar_hotel_commission !== null
          ? Number(settings.sellpar_hotel_commission)
          : 25;
      this.show_sellpar_settings_dialog = true;
    },
    saveSellparSettings() {
      if (!this.isSellparSettingsValid) return;
      this.saving_sellpar_settings = true;
      const hotelPayload = {
        ...this.current_hotel,
        settings: {
          ...((this.current_hotel && this.current_hotel.settings) || {}),
          sellpar_hotel_tax: Number(this.sellpar_hotel_tax),
          sellpar_hotel_commission: Number(this.sellpar_hotel_commission)
        }
      };
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_hotel"),
        color: "secondary"
      });
      this.$store
        .dispatch("updateHotel", hotelPayload)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("modified_hotel"),
            color: "accent"
          });
          this.show_sellpar_settings_dialog = false;
          this.is_updating_sellpar = true;
          this.scheduleSellparPoll(2000);
        })
        .catch(() => {
          showError(this, "something_went_wrong");
        })
        .finally(() => {
          this.saving_sellpar_settings = false;
        });
    },
    clearSellparPolling() {
      if (this.sellpar_poll_timeout) {
        clearTimeout(this.sellpar_poll_timeout);
        this.sellpar_poll_timeout = null;
      }
    },
    scheduleSellparPoll(delay = 5000) {
      this.clearSellparPolling();
      this.sellpar_poll_timeout = setTimeout(() => {
        this.checkSellparUpdating();
      }, delay);
    },
    async checkSellparUpdating() {
      if (!this.current_hotel || !this.current_hotel.id) return;
      const hotel_id = this.current_hotel.id;
      try {
        if (this.sellpar_task_id) {
          const response = await HotelService.getTaskStatus(
            hotel_id,
            this.sellpar_task_id
          );
          const data = response && response.data;
          const status =
            data && (data.status || (data.task && data.task.status));
          const isFinished =
            status === "done" ||
            status === "finished" ||
            status === "success" ||
            status === "completed" ||
            status === "failed" ||
            status === "error" ||
            data === false ||
            data === null;
          if (isFinished) {
            this.onSellparUpdateFinished();
            return;
          }
          this.scheduleSellparPoll();
          return;
        }
        const response = await HotelService.getIsUpdatingSellpar(hotel_id);
        const data = response && response.data;
        const isUpdating = !!(data && data.is_updating);
        if (isUpdating) {
          this.is_updating_sellpar = true;
          this.sellpar_task_id = data.task_id || null;
          this.scheduleSellparPoll();
        } else if (this.is_updating_sellpar) {
          this.onSellparUpdateFinished();
        } else {
          this.is_updating_sellpar = false;
          this.sellpar_task_id = null;
        }
      } catch (e) {
        this.scheduleSellparPoll(10000);
      }
    },
    onSellparUpdateFinished() {
      this.clearSellparPolling();
      this.is_updating_sellpar = false;
      this.sellpar_task_id = null;
      this.$store.dispatch("setReloadSubscribedModules");
    },
    getColumnTitle(column_header) {
      if (column_header.group === "segment") {
        return this.segments.find(
          segment => segment.code === column_header.column
        ).name;
      } else if (column_header.group === "roomtype") {
        return this.roomTypes.find(
          roomType => roomType.code === column_header.column
        ).name;
      } else if (
        column_header.group === "cityMKD" &&
        column_header.column === "MKD-Final" &&
        this.density === "compact"
      ) {
        return this.$t("cityMKD");
      } else if (
        column_header.group === "pickup_0" &&
        this.density === "compact"
      ) {
        if (column_header.column === "sold_rooms") {
          return "PU 0 RN";
        } else if (column_header.column === "avg_price") {
          return "PU 0 ADR";
        }
      } else if (
        column_header.group.includes("pickup_") &&
        column_header.group !== "pickup_0" &&
        this.density === "compact"
      ) {
        const pickupNumber = this.current_hotel.settings.pickup_spots[
          column_header.group
        ];
        if (column_header.column === "sold_rooms") {
          return `PU ${pickupNumber} RN`;
        } else if (column_header.column === "avg_price") {
          return `PU ${pickupNumber} ADR`;
        }
      } else {
        return this.$t(column_header.column);
      }
    }
  },
  computed: {
    group_names() {
      return this.configStrategyTable && Object.keys(this.configStrategyTable);
    },
    group_of_columns() {
      return (
        (this.configStrategyTable &&
          Object.keys(this.configStrategyTable).reduce((prev, curr) => {
            prev[curr] = Object.keys(
              this.configStrategyTable[curr].columns
            ).filter(
              columnKey =>
                !this.configStrategyTable[curr].columns[columnKey].hidden
            );
            return prev;
          }, {})) ||
        undefined
      );
    },
    column_headers() {
      const groups_and_columns = [];
      if (this.group_of_columns) {
        for (const group in this.group_of_columns) {
          this.group_of_columns[group].forEach(column => {
            groups_and_columns.push({ group, column });
          });
        }
      }
      return groups_and_columns;
    },
    last_column_of_each_group() {
      const column_headers = [...this.column_headers].reverse();
      return column_headers.reduce((prev, curr) => {
        if (!prev[curr.group]) {
          prev[curr.group] = curr.column;
        }
        return prev;
      }, {});
    },
    non_sticky_column_headers() {
      const non_sticky_column_headers = [];
      if (this.column_headers) {
        this.column_headers.forEach(column => {
          if (
            !this.sticky_columns.find(
              sticky_column =>
                column.group === sticky_column.group &&
                column.column === sticky_column.column
            )
          ) {
            non_sticky_column_headers.push(column);
          }
        });
      }
      // Añadimos el OTB al principio de la lista si existe en la configuración
      if (this.configStrategyTable["OTB"]) {
        non_sticky_column_headers.unshift({ group: "OTB", column: "adr" });
        if (this.density === "default") {
          non_sticky_column_headers.unshift({
            group: "OTB",
            column: "occupied"
          });
        }
        non_sticky_column_headers.unshift({
          group: "OTB",
          column: "occupancy"
        });
      }

      return non_sticky_column_headers;
    },
    sticky_group_columns() {
      return default_sticky_group_columns;
    },
    last_sticky_group_column() {
      return this.sticky_group_columns[this.sticky_group_columns.length - 1];
    },
    sticky_columns() {
      return (
        this.column_headers &&
        this.column_headers.filter(column =>
          this.sticky_group_columns.includes(column.group)
        )
      );
    },
    last_sticky_column() {
      return this.sticky_columns[this.sticky_columns.length - 1];
    },
    applyChangesList() {
      // Return array of selected prices from our reactive tracking object, sorted by date
      return Object.values(this.selected_prices).sort((a, b) => {
        return Vue.moment(a.date).diff(Vue.moment(b.date));
      });
    },
    applySuggestionsList() {
      // Return array of selected suggestions from our reactive tracking object, sorted by date
      return Object.values(this.selected_suggestions).sort((a, b) => {
        return Vue.moment(a.date).diff(Vue.moment(b.date));
      });
    },
    applyCombinedList() {
      // Combine both lists and add a type identifier
      const changes = this.applyChangesList.map(item => ({
        ...item,
        type: "change"
      }));
      const suggestions = this.applySuggestionsList.map(item => ({
        ...item,
        type: "suggestion"
      }));

      // Merge and sort by date
      return [...changes, ...suggestions].sort((a, b) => {
        return Vue.moment(a.date).diff(Vue.moment(b.date));
      });
    },
    sellparSettingsRules() {
      return [
        v =>
          (v !== "" && v !== null && v !== undefined) ||
          this.$t("validation_required"),
        v => (Number(v) >= 0 && Number(v) <= 100) || "0 - 100"
      ];
    },
    isSellparSettingsValid() {
      const tax = Number(this.sellpar_hotel_tax);
      const commission = Number(this.sellpar_hotel_commission);
      return (
        !isNaN(tax) &&
        tax >= 0 &&
        tax <= 100 &&
        !isNaN(commission) &&
        commission >= 0 &&
        commission <= 100
      );
    },
    showBulkUploadButton() {
      return (
        this.has_permission("rms_rates_bulk_upload") &&
        this.bulkPvpMlosStatus &&
        this.bulkPvpMlosStatus.status === "done"
      );
    },
    showBulkUploadProgress() {
      return (
        this.has_permission("rms_rates_bulk_upload") &&
        this.bulkPvpMlosStatus &&
        this.bulkPvpMlosStatus.status === "running"
      );
    },
    ...mapGetters({
      current_hotel: "current_hotel",
      l: "current_hotel_language",
      has_permission: "has_permission"
    }),
    ...mapState({
      bulkPvpMlosStatus: state => state.channelmanager.bulkPvpMlosStatus
    })
  },
  mounted() {
    this.getHotelComparativeDates();
    this.bulkImportStatus();
    this.checkSellparUpdating();
    this.$store.dispatch("fetchRmsAllKpisDailyCompset");
    this.$store.dispatch("fetchRmsAllKpisDailyPickup");
    this.$store.dispatch("fetchRmsPvpEvolutionDaily");
  },
  watch: {
    current_hotel() {
      this.bulkImportStatus();
      this.sellpar_task_id = null;
      this.is_updating_sellpar = false;
      this.checkSellparUpdating();
    },
    bulkPvpMlosStatus(newVal, oldVal) {
      if (!this.has_permission("rms_rates_bulk_upload")) return;
      this.$forceUpdate();
      if (newVal && newVal.status === "running") {
        setTimeout(() => {
          this.bulkImportStatus();
        }, 5000);
      } else if (oldVal && oldVal.status === "running") {
        this.$store.dispatch("setReloadSubscribedModules");
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bulk-upload-progress {
  width: 100px;
}
.sellpar-spin {
  animation: sellpar-spin 1s linear infinite;
}
@keyframes sellpar-spin {
  100% {
    transform: rotate(360deg);
  }
}
.grid-container {
  --heightHeaderRow: 25px;
  --widthColumnDate: 160px;
  --widthColumnColumns: 110px;
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 133px);

  .br-bolder {
    border-right: 1px solid #ccc !important;
    &:last-child {
      border-right: none !important;
    }
  }

  table {
    border-spacing: 0;
    thead {
      position: sticky;
      left: 0;
      top: 0;
      z-index: 3;
      tr {
        th {
          border: none;
          border-left: $borderType $borderColorV;
          height: var(--heightHeaderRow);
          width: var(--widthColumnColumns);
          min-width: var(--widthColumnColumns);
          background: white;
          color: #a0a0a0;
          .cell {
            height: var(--heightHeaderRow);
            font-size: 11px;
            font-weight: 500;
            text-align: center;
            padding: 0 5px;
            width: var(--widthColumnColumns);
            min-width: var(--widthColumnColumns);
            &.kpi-row {
              border-bottom: 2px solid transparent;
              border-top: 2px solid transparent;
            }
            &.kpi-hovered {
              border-bottom-color: #2d6ece;
            }
            &.kpi-rate-change {
              .title-column {
                width: 75px;
                white-space: nowrap !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
              }
            }
            .apply-prices-btn {
              height: 15px !important;
              width: 15px !important;
              .v-icon {
                height: 100%;
                width: 100%;
              }
            }
          }
          &:nth-child(1) {
            position: sticky;
            left: 0;
            width: var(--widthColumnDate);
            min-width: var(--widthColumnDate);
            z-index: 3;
            > .cell {
              width: var(--widthColumnDate);
              min-width: var(--widthColumnDate);
            }
          }
          &.header-otb {
            position: sticky;
            z-index: 3;
          }
          &.header-otb:nth-child(2) {
            border-left: none !important;
            left: calc(var(--widthColumnDate) + 1px);
          }
          &.header-otb:nth-child(3) {
            left: calc(
              var(--widthColumnDate) + var(--widthColumnColumns) + 1px
            );
          }
          &.header-otb:nth-child(4) {
            box-shadow: 2px 0px 0px 0px rgba(0, 0, 0, 0.11),
              4px 0px 0px 0px rgba(0, 0, 0, 0.07);
            left: calc(
              var(--widthColumnDate) + var(--widthColumnColumns) * 2 + 1px
            );
          }
          &.br-bolder + th {
            border-left: none !important;
          }
          &.br-bolder:nth-child(1) {
            border-right-width: 2px !important;
          }
        }
        &.group-row {
          th {
            border-left: none !important;
            &.group-otb {
              position: sticky;
              left: calc(var(--widthColumnDate) + 0px);
              z-index: 3;
            }
          }
        }
        &.header-row {
          box-shadow: 1px 0 0 0 rgba(0, 0, 0, 0.14),
            0 1px 0 0 rgba(0, 0, 0, 0.15), 2px 0 0 0 rgba(0, 0, 0, 0.11),
            0 2px 0 0 rgba(0, 0, 0, 0.12), 4px 0 0 0 rgba(0, 0, 0, 0.07),
            0 4px 0 0 rgba(0, 0, 0, 0.08);
          th {
            &:nth-child(1) {
              border-left: none !important;
              z-index: 4;
            }
          }
        }
      }
    }
    .sticky-columns-shadow {
      box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.15),
        2px 0px 0px 0px rgba(0, 0, 0, 0.12), 4px 0px 0px 0px rgba(0, 0, 0, 0.08);
    }
  }

  // Compact density
  &.density-compact {
    --widthColumnColumns: 60px;
    .column-group {
      font-size: 8px !important;
      line-height: 1 !important;
    }
    tr {
      th {
        top: 0;
        > .cell {
          height: 20px;
          font-size: 10px !important;
          line-height: 1.2 !important;
        }
      }
      &.header-row {
        th {
          top: 0;
        }
      }
    }
    ::v-deep td {
      height: inherit;
      width: inherit;
      min-width: inherit;
      padding: 0 !important;
      &:nth-child(1) {
        padding-left: 3px !important;
        .cell {
          min-height: auto;
        }
      }
      .percent-progress {
        bottom: 0px;
      }
      .cell {
        margin: 0 !important;
        height: inherit;
        text-align: center !important;
        .top-line {
          padding-top: 0 !important;
          justify-content: center !important;
          .kpi-wrapper-2 {
            padding: 4px 0 !important;
          }
        }
        .px-1 {
          padding: 0 !important;
        }
        .input-suggestion,
        .input-price {
          .v-input__append-inner {
            .v-input__icon {
              height: inherit !important;
              width: inherit !important;
              min-width: inherit !important;
              max-width: inherit !important;
              .v-icon {
                font-size: 12px !important;
              }
            }
          }
          .v-input__control {
            .v-input__slot {
              padding-left: 3px !important;
            }
            input {
              padding: 1px 0 !important;
            }
          }
        }
        .a-rms-apply-prices,
        .a-pricing-strategy-apply-strategy {
          padding: 0 !important;
        }
        .main-btn {
          padding: 1.5px 3px 2px;
        }
      }
    }
  }
}
</style>
